/**
 * <product-360-viewer>
 *
 * Turns a product's 360° spin video into a drag-to-scrub viewer, following the
 * exact same "deferred media" pattern as the rest of the gallery (see global.js
 * DeferredMedia and product-model.js ProductModel) so it plays nicely with the
 * existing gallery/modal/pauseAllMedia machinery.
 *
 * Behavior:
 * - Starts buffering only once the element enters the viewport (IntersectionObserver),
 *   instead of the stock "click poster to load" gate used by regular videos.
 * - Once loaded, the video autoplays on loop (muted) so it "spins" on its own.
 * - On the user's first drag/keyboard interaction, autoplay is paused and the
 *   video's currentTime is driven manually ("scrubbing").
 * - Respects prefers-reduced-motion: no automatic spin, but manual drag still works.
 * - Shows a short "drag to rotate" hint on first load, remembered in localStorage
 *   so it never reappears once dismissed.
 */
if (!customElements.get('product-360-viewer')) {
  const HINT_SEEN_KEY = 'aimerVideo360HintSeen';
  const KEYBOARD_STEP = 0.02; // ~2% of the video duration per arrow key press
  const DRAG_THRESHOLD = 6; // px of movement before an axis (horizontal vs vertical) is locked in

  customElements.define(
    'product-360-viewer',
    class Product360Viewer extends DeferredMedia {
      constructor() {
        super();
        this.progress = 0; // current scrub position, 0..1 fraction of the video duration
        this.dragState = null;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.hintDismissed = false;

        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerEnd = this.onPointerEnd.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
      }

      connectedCallback() {
        this.setupAutoLoad();
      }

      disconnectedCallback() {
        if (this.intersectionObserver) this.intersectionObserver.disconnect();
        this.pendingSeekProgress = null;
      }

      setupAutoLoad() {
        if (this.getAttribute('loaded')) return;

        if (!('IntersectionObserver' in window)) {
          this.loadContent(false);
          return;
        }

        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            if (!entries[0].isIntersecting) return;
            this.intersectionObserver.disconnect();
            this.loadContent(false);
          },
          { rootMargin: '200px 0px', threshold: 0.1 }
        );
        this.intersectionObserver.observe(this);
      }

      loadContent(focus = true) {
        if (this.getAttribute('loaded')) return;
        super.loadContent(focus);

        this.video = this.querySelector('video.product-360-viewer__video');
        if (!this.video) return;

        if (this.reducedMotion) {
          // Keep the spin video available for manual scrubbing, just don't auto-play it.
          this.video.loop = false;
          this.video.pause();
        } else {
          // Don't rely on the base DeferredMedia "force autoplay for Safari" workaround here:
          // it only calls .play() when `getAttribute('autoplay')` is truthy, but a bare
          // `<video autoplay>` (no value) makes getAttribute() return "" — which is falsy in
          // JS — so that inherited check silently never fires for this element. Native
          // autoplay-on-insert is also unreliable for a video appended via cloneNode() well
          // after page load (e.g. triggered later by IntersectionObserver), so play explicitly.
          const playPromise = this.video.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => {
              // Autoplay can still be legitimately blocked (e.g. data-saver mode) — the poster
              // frame just stays visible and manual drag/keyboard control keeps working.
            });
          }
        }

        this.video.addEventListener(
          'loadedmetadata',
          () => {
            this.progress = this.video.duration ? this.video.currentTime / this.video.duration : 0;
            this.updateAriaValue();
          },
          { once: true }
        );

        // As soon as the browser finishes decoding to the position we last asked for, seek
        // straight to whatever the finger's latest position is by then — see scheduleSeek().
        this.video.addEventListener('seeked', () => {
          this.classList.remove('is-seeking');
          this.flushPendingSeek();
        });
        // This video's keyframe spacing means an arbitrary seek can take several hundred
        // ms on a phone to actually land (confirmed against a real recording) — nothing
        // left to gain by pacing calls to `currentTime` differently, that ceiling is the
        // video encoding itself, not JS timing. What we CAN still do: while seeking, dip
        // the video slightly so the wait reads as a soft transition rather than a hard,
        // "broken-looking" freeze between snapped frames.
        this.video.addEventListener('seeking', () => this.classList.add('is-seeking'));

        this.setupHint();
        this.bindInteractions();
      }

      // --- "Drag to rotate" hint -------------------------------------------------

      setupHint() {
        this.hintElement = this.parentElement && this.parentElement.querySelector(':scope > [data-video-360-hint]');
        if (!this.hintElement) return;

        if (this.hasSeenHint()) {
          this.hintElement.hidden = true;
          this.hintDismissed = true;
          return;
        }

        this.hintElement.hidden = false;
        window.requestAnimationFrame(() => this.hintElement.classList.add('is-visible'));
        this.hintTimeout = window.setTimeout(() => this.dismissHint(), 4000);
      }

      hasSeenHint() {
        try {
          return window.localStorage.getItem(HINT_SEEN_KEY) === '1';
        } catch (error) {
          return false;
        }
      }

      dismissHint() {
        if (this.hintDismissed || !this.hintElement) return;
        this.hintDismissed = true;
        window.clearTimeout(this.hintTimeout);
        this.hintElement.classList.remove('is-visible');
        const hintElement = this.hintElement;
        window.setTimeout(() => {
          hintElement.hidden = true;
        }, 300);
        try {
          window.localStorage.setItem(HINT_SEEN_KEY, '1');
        } catch (error) {
          // Storage unavailable (e.g. Safari private mode) — hint may reappear next visit, non-critical.
        }
      }

      // --- Drag / touch scrub ------------------------------------------------------

      bindInteractions() {
        this.addEventListener('pointerdown', this.onPointerDown);
        this.addEventListener('pointermove', this.onPointerMove);
        this.addEventListener('pointerup', this.onPointerEnd);
        this.addEventListener('pointercancel', this.onPointerEnd);
        this.addEventListener('keydown', this.onKeyDown);
      }

      onPointerDown(event) {
        if (!this.video || !this.video.duration) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;

        // Deliberately does NOT touch playback yet. A plain tap (pointerdown immediately
        // followed by pointerup, no real movement) is not a drag — pausing here
        // unconditionally meant a single accidental tap froze the spin permanently, which
        // read as "the video is just a static photo". Autoplay keeps running undisturbed
        // until onPointerMove below confirms this is actually a horizontal drag.
        this.dragState = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          startProgress: this.progress,
          axisLocked: null, // null = undecided yet, 'x' = horizontal drag, 'y' = vertical scroll
        };
      }

      onPointerMove(event) {
        if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;

        const deltaX = event.clientX - this.dragState.startX;
        const deltaY = event.clientY - this.dragState.startY;

        if (this.dragState.axisLocked === null) {
          if (Math.abs(deltaX) < DRAG_THRESHOLD && Math.abs(deltaY) < DRAG_THRESHOLD) return;
          this.dragState.axisLocked = Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y';
          if (this.dragState.axisLocked === 'x') {
            // Only now do we know the user actually means to rotate the piece — take over
            // from autoplay.
            this.dismissHint();
            this.video.pause();
            this.classList.add('is-dragging');
            // Buffer the rest of the video eagerly now that we know the visitor is
            // actively scrubbing — seeking to a not-yet-downloaded position adds network
            // latency on top of the normal seek cost, which reads as extra stutter.
            if (this.video.preload !== 'auto') this.video.preload = 'auto';
            try {
              this.setPointerCapture(event.pointerId);
            } catch (error) {
              // Pointer capture isn't critical — dragging still works without it.
            }
          }
        }

        // Vertical gesture: this is a page scroll, not a rotate — let the browser handle it
        // (touch-action: pan-y on the element already keeps native scrolling smooth).
        if (this.dragState.axisLocked !== 'x') return;

        event.preventDefault();
        const width = this.clientWidth || 1;
        // Negated: dragging right should make the piece turn to follow the finger (the side
        // facing you swings right), which plays this footage backwards, not forwards.
        let newProgress = this.dragState.startProgress - deltaX / width;
        newProgress = ((newProgress % 1) + 1) % 1; // wrap around, supports spinning past a full turn
        this.scheduleSeek(newProgress);
      }

      onPointerEnd(event) {
        if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;
        this.classList.remove('is-dragging');
        this.dragState = null;
      }

      // Touch/mouse move events can fire far faster (especially on 120Hz touchscreens)
      // than a compressed video can actually seek — each seek means locating the nearest
      // keyframe and decoding forward to the exact frame, which takes real time (and on a
      // phone, can easily take longer than a single animation frame). Calling
      // `video.currentTime = ...` on every pointermove — or even once per animation frame
      // — can still queue up more seeks than the decoder finishes in time, which is what
      // "travado"/stuck-feeling scrubbing actually is: the video is always catching up to
      // stale requests instead of the finger's current position.
      //
      // Instead, only the lightweight bits (the tracked progress value and the aria
      // attribute) update immediately on every move, for instant visual/UI feedback. The
      // expensive part — the actual video seek — is paced by the browser's own `seeked`
      // event: at most one seek is ever in flight, and the moment it completes, the next
      // one fires straight to the finger's latest position (skipping anything in between).
      // This adapts to however fast the device can actually seek, rather than assuming a
      // fixed rate it might not keep up with.
      scheduleSeek(progress) {
        this.progress = progress;
        this.updateAriaValue();
        this.pendingSeekProgress = progress;
        this.flushPendingSeek();
      }

      flushPendingSeek() {
        if (!this.video || this.video.seeking || this.pendingSeekProgress == null) return;
        const target = this.pendingSeekProgress * this.video.duration;
        this.pendingSeekProgress = null;
        this.video.currentTime = target;
      }

      onKeyDown(event) {
        if (!this.video || !this.video.duration) return;
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

        event.preventDefault();
        this.dismissHint();
        this.video.pause();

        // Matches the drag direction above: ArrowRight = same rotation as dragging right.
        let newProgress = this.progress + (event.key === 'ArrowRight' ? -KEYBOARD_STEP : KEYBOARD_STEP);
        newProgress = ((newProgress % 1) + 1) % 1;
        this.setProgress(newProgress);
      }

      setProgress(progress) {
        this.progress = progress;
        this.video.currentTime = progress * this.video.duration;
        this.updateAriaValue();
      }

      updateAriaValue() {
        this.setAttribute('aria-valuenow', Math.round(this.progress * 100));
      }
    }
  );
}
